import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Select } from 'antd';
import type { Paged, StudioQueueKind, StudioRef } from '../contract';
import {
  STUDIO_PAGE_LIMIT,
  StudioViewer,
  getRequestsHistory,
  getRequestsQueue,
  useStudioLoad,
} from '../api';
import {
  EMPTY_TEXT,
  QUEUE_KIND_OPTIONS,
  REQUEST_STATUS_FILTER_OPTIONS,
  RequestStatusFilter,
} from '../labels';
import RequestsTable, { RequestsTableRow } from '../components/requests-table';
import { StudioErrorBlock, StudioFilterError, studioTitle, useClampPage } from './studio-list-tab';
import styles from '../styles.module.scss';

export interface RequestsTabProps {
  viewer: StudioViewer;
  /** Studio cho bộ lọc (trang tải một lần); có thể rỗng khi chưa tải xong. */
  studios: StudioRef[];
  /** Lỗi tải danh sách Studio cho bộ lọc (hiện cạnh bộ lọc kèm "Thử lại"). */
  studiosError?: any;
  onReloadStudios?: () => void;
  /** Sau mỗi thao tác duyệt / từ chối / ghi lại (trang tải lại số trên nhãn tab). */
  onChanged?: () => void;
}

const QUEUE_KINDS: StudioQueueKind[] = ['STUDIO_MEMBER', 'STUDIO_KC'];
const STATUS_VALUES = REQUEST_STATUS_FILTER_OPTIONS.map((o) => o.value);

const readKind = (raw: string | null): StudioQueueKind | undefined =>
  QUEUE_KINDS.includes(raw as StudioQueueKind) ? (raw as StudioQueueKind) : undefined;
const readStatus = (raw: string | null): RequestStatusFilter =>
  STATUS_VALUES.includes(raw as RequestStatusFilter) ? (raw as RequestStatusFilter) : 'QUEUE';
const readId = (raw: string | null): number | undefined => {
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : undefined;
};

/**
 * Tab "Đề nghị chờ duyệt" (`/studios?tab=requests`, mục 8.7.2). Bộ lọc: loại việc (mặc định Tất cả = không gửi
 * `kind`), Studio (mặc định Tất cả; `?studioId=` từ ô đếm của danh sách), trạng thái (mặc định Chờ Admin).
 * "Chờ Admin" đọc hàng đợi Q2 (gồm cả "Đã duyệt – chưa ghi được số"), có ô chọn để duyệt hàng loạt (Admin);
 * trạng thái khác đọc lịch sử Q3. Bộ lọc nằm trên URL để link từ danh sách Studio mở đúng bộ lọc.
 */
const RequestsTab = ({
  viewer,
  studios,
  studiosError,
  onReloadStudios,
  onChanged,
}: RequestsTabProps) => {
  const [params, setParams] = useSearchParams();
  const kind = readKind(params.get('kind'));
  const status = readStatus(params.get('status'));
  const studioId = readId(params.get('studioId'));
  const [page, setPage] = useState(1);
  const isQueue = status === 'QUEUE';

  const setParam = (key: string, value?: string | number | null) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === undefined || value === null || value === '') next.delete(key);
        else next.set(key, String(value));
        return next;
      },
      { replace: true }
    );
    setPage(1);
  };

  const load = useStudioLoad<Paged<RequestsTableRow>>(
    () =>
      isQueue
        ? getRequestsQueue({ kind, studioId, page, limit: STUDIO_PAGE_LIMIT })
        : getRequestsHistory({ status, kind, studioId, page, limit: STUDIO_PAGE_LIMIT }),
    [status, kind, studioId, page],
    // Hàng đợi (Q2) và lịch sử (Q3) khác kiểu dòng: đổi qua lại thì bỏ dữ liệu cũ.
    { resetKey: isQueue }
  );
  // Duyệt / từ chối dòng cuối của trang cuối: lùi trang thay vì kẹt ở trang rỗng.
  useClampPage(page, setPage, load.data, load.loading);

  const studioOptions = studios.map((s) => ({ value: s.id, label: studioTitle(s) }));
  if (studioId && !studioOptions.some((o) => o.value === studioId)) {
    studioOptions.unshift({ value: studioId, label: `Studio #${studioId}` });
  }

  const afterAction = () => {
    load.reload();
    onChanged?.();
  };

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <Select<StudioQueueKind>
            allowClear
            placeholder="Tất cả loại việc"
            value={kind}
            options={QUEUE_KIND_OPTIONS}
            style={{ width: 230 }}
            onChange={(value) => setParam('kind', value)}
            aria-label="Loại việc"
          />
          <Select<number>
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Tất cả Studio"
            value={studioId}
            options={studioOptions}
            style={{ width: 220 }}
            notFoundContent={studiosError ? 'Không tải được danh sách Studio.' : undefined}
            onChange={(value) => setParam('studioId', value)}
            aria-label="Studio"
          />
          <Select<RequestStatusFilter>
            value={status}
            options={REQUEST_STATUS_FILTER_OPTIONS}
            style={{ width: 230 }}
            onChange={(value) => setParam('status', value === 'QUEUE' ? null : value)}
            aria-label="Trạng thái"
          />
        </div>
      </div>
      {studiosError && <StudioFilterError error={studiosError} onRetry={onReloadStudios} />}
      {load.error && <StudioErrorBlock error={load.error} onRetry={load.reload} />}
      {!(load.error && !load.data) && (
        <RequestsTable
          rows={load.data?.data || []}
          loading={load.loading}
          mode={isQueue ? 'queue' : 'history'}
          selectable={isQueue && viewer.canAdmin}
          onChanged={afterAction}
          emptyText={isQueue ? EMPTY_TEXT.requestsQueue : EMPTY_TEXT.requestsMine}
          pagination={{
            page,
            pageSize: STUDIO_PAGE_LIMIT,
            total: load.data?.metadata?.total ?? 0,
            onChange: setPage,
          }}
        />
      )}
    </div>
  );
};

export default RequestsTab;
